import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowTrendUp,
  faBullseye,
  faChartLine,
  faTrophy,
  faClock,
  faBolt,
  faPersonRunning,
  faMedal,
  faStar,
  faFire,
  faMountain,
  faHeart,
  faStopwatch,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import api from '../lib/api';
import { logger } from '../lib/logger';
import { estimateCalories, DEFAULT_WEIGHT_KG } from '../lib/calories';
import ActivityHeatmap from '../components/analytics/ActivityHeatmap';

const iconMap = {
  'faPersonRunning': faPersonRunning,
  'faMedal': faMedal,
  'faTrophy': faTrophy,
  'faBolt': faBolt,
  'faStopwatch': faStopwatch,
  'faFire': faFire,
  'faStar': faStar,
  'faChartLine': faChartLine,
  'faMountain': faMountain,
  'faClock': faClock,
  'faHeart': faHeart
};

// Helper functions
const parseDuration = (interval) => {
  if (!interval) return 0;
  const parts = interval.split(':');
  if (parts.length === 3) {
    return parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseInt(parts[2]);
  }
  return 0;
};

const formatPace = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const roundDistance = (data) => {
  data.forEach(d => { d.distance = Math.round(d.distance * 100) / 100; });
  return data;
};

const processRunsForChart = (runs, range) => {
  if (range === 'week') {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const dayData = days.map(day => ({ name: day, distance: 0, runs: 0 }));
    runs.forEach(run => {
      const date = new Date(run.run_date);
      const dayIndex = date.getDay();
      dayData[dayIndex].distance += parseFloat(run.distance_km) || 0;
      dayData[dayIndex].runs += 1;
    });
    return roundDistance(dayData);
  } else if (range === 'month') {
    const weeks = [
      { name: 'Week 1', distance: 0, runs: 0 },
      { name: 'Week 2', distance: 0, runs: 0 },
      { name: 'Week 3', distance: 0, runs: 0 },
      { name: 'Week 4', distance: 0, runs: 0 }
    ];
    runs.forEach(run => {
      const date = new Date(run.run_date);
      const weekIndex = Math.min(Math.floor(date.getDate() / 7), 3);
      weeks[weekIndex].distance += parseFloat(run.distance_km) || 0;
      weeks[weekIndex].runs += 1;
    });
    return roundDistance(weeks);
  } else if (range === 'year') {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthData = months.map(month => ({ name: month, distance: 0, runs: 0 }));
    runs.forEach(run => {
      const date = new Date(run.run_date);
      const monthIndex = date.getMonth();
      monthData[monthIndex].distance += parseFloat(run.distance_km) || 0;
      monthData[monthIndex].runs += 1;
    });
    return roundDistance(monthData);
  } else {
    const years = {};
    runs.forEach(run => {
      const year = new Date(run.run_date).getFullYear();
      if (!years[year]) {
        years[year] = { name: year.toString(), distance: 0, runs: 0 };
      }
      years[year].distance += parseFloat(run.distance_km) || 0;
      years[year].runs += 1;
    });
    return roundDistance(Object.values(years).sort((a, b) => a.name - b.name));
  }
};

const Analytics = () => {
  const authUser = useSelector((state) => state.auth.user);
  const [timeRange, setTimeRange] = useState('month');
  const [isLoading, setIsLoading] = useState(true);

  const isMountedRef = useRef(true);

  const [stats, setStats] = useState({
    totalDistance: 0,
    totalRuns: 0,
    avgPace: '-',
    longestRun: 0,
    bestPace: '-',
    totalTime: '0h 0m',
    caloriesBurned: 0
  });
  const [chartData, setChartData] = useState([]);
  const [allAchievements, setAllAchievements] = useState([]);
  const [userAchievementIds, setUserAchievementIds] = useState(new Set());
  const [achievementFilter, setAchievementFilter] = useState('all');

  const [heatmapRuns, setHeatmapRuns] = useState([]);
  const [weightKg, setWeightKg] = useState(DEFAULT_WEIGHT_KG);

  const [paceDistribution, setPaceDistribution] = useState([
    { name: '<4:00', value: 0, color: '#FF6FD8' },
    { name: '4:00-5:00', value: 0, color: '#FFD36E' },
    { name: '5:00-6:00', value: 0, color: '#5AB9EA' },
    { name: '6:00+', value: 0, color: '#FF9AA2' }
  ]);

  // Fetch user stats
  const fetchStats = useCallback(async () => {
    if (!authUser?.id) return;

    try {
      const [userStats, allRuns, settings] = await Promise.all([
        api.profiles.getStats(authUser.id),
        api.runs.list(null, 'asc'),
        api.profiles.getSettings(authUser.id).catch(() => null),
      ]);

      if (!isMountedRef.current) return;

      const userWeight = settings?.weight_kg ?? DEFAULT_WEIGHT_KG;
      setWeightKg(userWeight);

      if (userStats) {
        const totalSeconds = parseDuration(userStats.total_duration);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);

        const totalCalories = (allRuns || []).reduce((sum, run) => {
          return sum + estimateCalories(parseFloat(run.distance_km) || 0, run.duration, userWeight);
        }, 0);

        setStats({
          totalDistance: parseFloat(userStats.total_distance_km) || 0,
          totalRuns: userStats.total_runs || 0,
          avgPace: userStats.avg_pace_seconds ? formatPace(userStats.avg_pace_seconds) : '-',
          longestRun: parseFloat(userStats.longest_run_km) || 0,
          bestPace: userStats.best_pace_seconds ? formatPace(userStats.best_pace_seconds) : '-',
          totalTime: `${hours}h ${minutes}m`,
          caloriesBurned: totalCalories
        });
      }
    } catch (error) {
      logger.warn('Error fetching stats (non-blocking):', error);
    }
  }, [authUser?.id]);

  // Fetch runs for chart data
  const fetchRunsForChart = useCallback(async (currentTimeRange) => {
    if (!authUser?.id) return;

    try {
      const now = new Date();
      let startDate;
      
      switch (currentTimeRange) {
        case 'week':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'month':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case 'year':
          startDate = new Date(now.getFullYear(), 0, 1);
          break;
        default:
          startDate = new Date('2020-01-01');
      }

      const runs = await api.runs.list(startDate.toISOString().split('T')[0], 'asc');

      if (!isMountedRef.current) return;

      if (runs && runs.length > 0) {
        const processedData = processRunsForChart(runs, currentTimeRange);
        setChartData(processedData);

        const distribution = calculatePaceDistribution(runs);
        setPaceDistribution(distribution);
      } else {
        setChartData([]);
        setPaceDistribution([
          { name: '<4:00', value: 0, color: '#FF6FD8' },
          { name: '4:00-5:00', value: 0, color: '#FFD36E' },
          { name: '5:00-6:00', value: 0, color: '#5AB9EA' },
          { name: '6:00+', value: 0, color: '#FF9AA2' }
        ]);
      }
    } catch (error) {
      logger.warn('Error fetching runs for chart (non-blocking):', error);
    }
  }, [authUser?.id]);

  const fetchHeatmapRuns = useCallback(async () => {
    if (!authUser?.id) return;

    try {
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
      const runs = await api.runs.list(oneYearAgo.toISOString().split('T')[0], 'asc');

      if (isMountedRef.current && runs) {
        setHeatmapRuns(runs);
      }
    } catch (error) {
      logger.warn('Error fetching heatmap runs (non-blocking):', error);
    }
  }, [authUser?.id]);

  const fetchAchievements = useCallback(async () => {
    if (!authUser?.id) return;

    try {
      const [achievements, userAchievements] = await Promise.all([
        api.profiles.getAllAchievements(),
        api.profiles.getUserAchievements(authUser.id),
      ]);

      if (!isMountedRef.current) return;

      const earnedMap = new Map();
      (userAchievements || []).forEach(ua => {
        earnedMap.set(ua.achievement_id, ua.earned_at);
      });

      setUserAchievementIds(new Set(earnedMap.keys()));

      const formattedAchievements = (achievements || []).map(a => ({
        id: a.id,
        title: a.title,
        description: a.description,
        icon: iconMap[a.icon] || faTrophy,
        earned: earnedMap.has(a.id),
        date: earnedMap.has(a.id) ? new Date(earnedMap.get(a.id)).toLocaleDateString() : null,
        category: a.category
      }));

      setAllAchievements(formattedAchievements);
    } catch (error) {
      logger.warn('Error fetching achievements (non-blocking):', error);
    }
  }, [authUser?.id]);

  useEffect(() => {
    isMountedRef.current = true;
    
    const loadInitialData = async () => {
      setIsLoading(true);
      await Promise.allSettled([
        fetchStats(),
        fetchHeatmapRuns(),
        fetchAchievements()
      ]);
      
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    };

    loadInitialData();
    
    return () => {
      isMountedRef.current = false;
    };
  }, [fetchStats, fetchHeatmapRuns, fetchAchievements]);

  useEffect(() => {
    fetchRunsForChart(timeRange);
  }, [timeRange, fetchRunsForChart]);

  const calculatePaceDistribution = (runs) => {
    const distribution = [
      { name: '<4:00', value: 0, color: '#FF6FD8' },
      { name: '4:00-5:00', value: 0, color: '#FFD36E' },
      { name: '5:00-6:00', value: 0, color: '#5AB9EA' },
      { name: '6:00+', value: 0, color: '#FF9AA2' }
    ];

    let validRuns = 0;
    runs.forEach(run => {
      let paceMin = null;

      if (run.pace) {
        const parts = run.pace.split(':').map(Number);
        if (parts.length === 2) paceMin = parts[0] + parts[1] / 60;
      } else if (run.duration && run.distance_km > 0) {
        const durParts = run.duration.split(':').map(Number);
        if (durParts.length === 3) {
          const totalMin = durParts[0] * 60 + durParts[1] + durParts[2] / 60;
          paceMin = totalMin / parseFloat(run.distance_km);
        }
      }

      if (paceMin !== null) {
        validRuns++;
        if (paceMin < 4) distribution[0].value++;
        else if (paceMin < 5) distribution[1].value++;
        else if (paceMin < 6) distribution[2].value++;
        else distribution[3].value++;
      }
    });

    if (validRuns > 0) {
      distribution.forEach(d => {
        d.value = Math.round((d.value / validRuns) * 100);
      });
    }

    return distribution;
  };

  if (isLoading) {
    return (
      <div className="p-4 max-w-6xl mx-auto flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-primary-pink animate-spin mb-4" />
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-heading font-semibold mb-2 bg-gradient-vibe bg-clip-text text-transparent">
          Analytics Dashboard
        </h1>
        <p className="text-gray-600 text-body">
          Complete running insights and goal tracking
        </p>
      </div>

      <div
        className="flex gap-2 mb-6 overflow-x-auto pb-2"
        style={{ overflowY: 'hidden' }}
      >
        {['week', 'month', 'year', 'all'].map((range) => (
          <button
            key={range}
            onClick={() => setTimeRange(range)}
            className={`px-4 py-2 rounded-xl font-medium transition-all duration-200 flex-shrink-0 hover:scale-[1.05] active:scale-[0.95] ${
              timeRange === range
                ? 'bg-gradient-vibe text-white shadow-lg'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {range.charAt(0).toUpperCase() + range.slice(1)}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-4 mb-6">
        {[
          { label: 'Distance', value: `${stats.totalDistance}km`, icon: faChartLine, color: 'text-primary-pink' },
          { label: 'Runs', value: stats.totalRuns, icon: faBullseye, color: 'text-secondary-blue' },
          { label: 'Avg Pace', value: stats.avgPace, icon: faBolt, color: 'text-primary-yellow' },
          { label: 'Best Pace', value: stats.bestPace, icon: faArrowTrendUp, color: 'text-secondary-pink' },
          { label: 'Longest', value: `${stats.longestRun}km`, icon: faTrophy, color: 'text-primary-pink' },
          { label: 'Time', value: stats.totalTime, icon: faClock, color: 'text-secondary-blue' },
          { label: 'Calories', value: `${(stats.caloriesBurned / 1000).toFixed(1)}k`, icon: faChartLine, color: 'text-primary-yellow' }
        ].map((stat) => (
          <div
            key={stat.label}
            className="scrapbook-container text-center p-3"
          >
            <FontAwesomeIcon icon={stat.icon} className={`w-5 h-5 ${stat.color} mx-auto mb-2`} />
            <div className="font-heading font-bold text-lg text-gray-900 mb-1">
              {stat.value}
            </div>
            <div className="text-xs text-gray-500">{stat.label}</div>
          </div>
        ))}
      </div>

      <div className="mb-6">
        <ActivityHeatmap runs={heatmapRuns} />
      </div>

      <div className="grid grid-cols-3 gap-6 mb-6">
        <div
          className="col-span-2 scrapbook-container"
          key={timeRange}
        >
          <div className="flex items-center gap-2 mb-4">
            <FontAwesomeIcon icon={faArrowTrendUp} className="w-5 h-5 text-primary-pink" />
            <h2 className="font-heading font-semibold text-lg">
              {timeRange === 'week' ? 'This Week' : 
               timeRange === 'month' ? 'This Month' : 
               timeRange === 'year' ? 'This Year' : 
               'All Time'} Distance Progress
            </h2>
          </div>
          
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" stroke="#666" fontSize={12} />
                <YAxis stroke="#666" fontSize={12} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e5e5e5',
                    borderRadius: '12px',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                  }}
                  formatter={(value, name) => [`${value} km`, name]}
                />
                <Line 
                  type="monotone" 
                  dataKey="distance" 
                  stroke="url(#gradient)" 
                  strokeWidth={3}
                  dot={{ fill: '#FF6FD8', strokeWidth: 2, r: 6 }}
                  activeDot={{ r: 8, stroke: '#FF6FD8', strokeWidth: 2 }}
                />
                <defs>
                  <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#FF6FD8" />
                    <stop offset="100%" stopColor="#FFD36E" />
                  </linearGradient>
                </defs>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="scrapbook-container">
          <div className="flex items-center gap-2 mb-4">
            <FontAwesomeIcon icon={faBolt} className="w-5 h-5 text-secondary-blue" />
            <h2 className="font-heading font-semibold text-lg">Pace Distribution</h2>
          </div>
          
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={paceDistribution}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="value"
                  stroke="none"
                >
                  {paceDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          
          <div className="space-y-2">
            {paceDistribution.map((item, index) => (
              <div key={index} className="flex items-center gap-2 text-sm">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-gray-600">{item.name}</span>
                <span className="text-gray-900 font-medium ml-auto">{item.value}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="scrapbook-container">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faMedal} className="w-5 h-5 text-primary-pink" />
            <h2 className="font-heading font-semibold text-lg">All Achievements</h2>
          </div>
          <span className="text-sm text-gray-500">
            {userAchievementIds.size}/{allAchievements.length} Earned
          </span>
        </div>

        <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
          {['all', 'earned', 'locked', 'Distance', 'Speed', 'Consistency', 'Milestones', 'Challenges', 'Health'].map((filter) => (
            <button
              key={filter}
              onClick={() => setAchievementFilter(filter)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 hover:scale-[1.05] active:scale-[0.95] ${
                achievementFilter === filter
                  ? 'bg-gradient-vibe text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {filter.charAt(0).toUpperCase() + filter.slice(1)}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {allAchievements.length === 0 ? (
            <div className="col-span-full text-center py-8 text-gray-500">
              No achievements available yet.
            </div>
          ) : allAchievements
            .filter(achievement => {
              if (achievementFilter === 'all') return true;
              if (achievementFilter === 'earned') return achievement.earned;
              if (achievementFilter === 'locked') return !achievement.earned;
              return achievement.category === achievementFilter;
            })
            .map((achievement) => (
              <div
                key={achievement.id}
                className={`relative p-4 rounded-xl border-2 transition-all ${
                  achievement.earned
                    ? 'bg-gradient-to-br from-white to-pink-50 border-primary-pink/30 shadow-md'
                    : 'bg-gray-50 border-gray-200 opacity-60'
                }`}
              >
                {achievement.earned && (
                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white text-xs shadow-lg">
                    ✓
                  </div>
                )}
                
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${
                  achievement.earned
                    ? 'bg-gradient-vibe'
                    : 'bg-gray-300'
                }`}>
                  <FontAwesomeIcon 
                    icon={achievement.icon} 
                    className={`w-6 h-6 ${achievement.earned ? 'text-white' : 'text-gray-500'}`} 
                  />
                </div>
                
                <h3 className={`font-semibold text-sm mb-1 ${
                  achievement.earned ? 'text-gray-900' : 'text-gray-500'
                }`}>
                  {achievement.title}
                </h3>
                
                <p className={`text-xs mb-2 ${
                  achievement.earned ? 'text-gray-600' : 'text-gray-400'
                }`}>
                  {achievement.description}
                </p>
                
                <div className="flex items-center justify-between">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    achievement.earned
                      ? 'bg-primary-pink/10 text-primary-pink'
                      : 'bg-gray-200 text-gray-500'
                  }`}>
                    {achievement.category}
                  </span>
                  {achievement.earned && achievement.date && (
                    <span className="text-xs text-gray-400">
                      {achievement.date}
                    </span>
                  )}
                </div>
              </div>
            ))
          }
        </div>
      </div>
    </div>
  );
};

export default Analytics;
